// Currency formatting helper for the customer-app.
//
// The backend stores prices as Decimal/Float unit-agnostic. Each tenant
// has a `currency` field on their Restaurant record (ISO-4217 code) that
// the restaurant-info endpoint surfaces alongside the menu. This helper
// turns (amount, currency) into the human-facing string.
//
// Conventions chosen to match local expectation, NOT Intl.NumberFormat
// defaults (those produce noisy output like "ETB 12.50" with a space and
// the full code, which looks unfamiliar to Ethiopian users):
//
//   - ETB  → "Br 12.50"   (Ethiopian Birr, "Br" symbol used in receipts)
//   - EUR  → "€12.50"     (Euro, symbol before, no space)
//   - USD  → "$12.50"     (future)
//
// Unknown codes fall back to "<CODE> 12.50" so a new currency from the
// backend at least renders something legible until the frontend catches
// up (added to ALLOWED_CURRENCIES in admin.py).
//
// All callers go through `formatPrice` — never call .toFixed() directly
// in render code. Centralising this means the eventual NL pilot can flip
// a tenant's currency without code changes anywhere except this file.

const FORMATTERS = {
  ETB: (n) => `Br ${n.toFixed(2)}`,
  EUR: (n) => `€${n.toFixed(2)}`,
  USD: (n) => `$${n.toFixed(2)}`,
};

export function formatPrice(amount, currency) {
  const num = Number(amount || 0);
  const code = (currency || "ETB").toUpperCase();
  const fmt = FORMATTERS[code];
  if (fmt) return fmt(num);
  // Unknown code: render the code + amount so it's still informative.
  return `${code} ${num.toFixed(2)}`;
}

// Plain symbol for use in inline labels ("Total in €:") — not for prices.
export function currencySymbol(currency) {
  const code = (currency || "ETB").toUpperCase();
  if (code === "ETB") return "Br";
  if (code === "EUR") return "€";
  if (code === "USD") return "$";
  return code;
}
